import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import "./BioPreview.scss";

const MAX_LINES = 2;
const LINE_HEIGHT_FALLBACK = 1.4;

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const trimToWordBoundary = (value) => {
  const text = value.trimEnd();
  const lastSpace = text.lastIndexOf(" ");
  if (lastSpace <= 0) return text;
  return text.slice(0, lastSpace).trimEnd();
};

const BioPreview = ({ text, onMore }) => {
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const measureTextRef = useRef(null);
  const measureSuffixRef = useRef(null);
  const [displayText, setDisplayText] = useState("");
  const [isTruncated, setIsTruncated] = useState(false);

  const normalizedText = useMemo(() => normalizeText(text), [text]);

  useLayoutEffect(() => {
    const wrapperEl = wrapperRef.current;
    const measureEl = measureRef.current;
    const measureTextEl = measureTextRef.current;
    const measureSuffixEl = measureSuffixRef.current;

    if (!wrapperEl || !measureEl || !measureTextEl || !measureSuffixEl) {
      return undefined;
    }

    const recompute = () => {
      if (!normalizedText) {
        setDisplayText("");
        setIsTruncated(false);
        return;
      }

      const measureStyle = window.getComputedStyle(measureEl);
      let lineHeightPx = parseFloat(measureStyle.lineHeight);
      if (Number.isNaN(lineHeightPx)) {
        const fontSizePx = parseFloat(measureStyle.fontSize || "16");
        lineHeightPx = fontSizePx * LINE_HEIGHT_FALLBACK;
      }
      const maxHeight = lineHeightPx * MAX_LINES;

      const setMeasureContent = (content, showSuffix) => {
        measureTextEl.textContent = content;
        measureSuffixEl.hidden = !showSuffix;
      };

      const fits = (content, showSuffix) => {
        setMeasureContent(content, showSuffix);
        return measureEl.scrollHeight <= maxHeight;
      };

      if (fits(normalizedText, false)) {
        setDisplayText(normalizedText);
        setIsTruncated(false);
        return;
      }

      let low = 0;
      let high = normalizedText.length;

      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const candidate = normalizedText.slice(0, mid).trimEnd();
        if (fits(candidate, true)) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }

      const hardCut = normalizedText.slice(0, low).trimEnd();
      const wordCut = trimToWordBoundary(hardCut);
      let finalText = wordCut.length >= Math.floor(hardCut.length * 0.7) ? wordCut : hardCut;

      while (finalText.length > 0 && !fits(finalText, true)) {
        const shorterWordCut = trimToWordBoundary(finalText);
        if (shorterWordCut.length > 0 && shorterWordCut !== finalText) {
          finalText = shorterWordCut;
          continue;
        }
        finalText = finalText.slice(0, -1).trimEnd();
      }

      setDisplayText(finalText);
      setIsTruncated(true);
    };

    recompute();

    const resizeObserver = new ResizeObserver(() => recompute());
    resizeObserver.observe(wrapperEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [normalizedText]);

  return (
    <div className="bio-preview" ref={wrapperRef}>
      <p className="bio-preview__measure" ref={measureRef} aria-hidden="true">
        <span ref={measureTextRef} />
        <span ref={measureSuffixRef} className="bio-preview__suffix" hidden>
          ... More
        </span>
      </p>
      <p className="bio-preview__text">
        {displayText}
        {isTruncated && (
          <>
            <span className="bio-preview__suffix" aria-hidden="true">... </span>
            <button
              type="button"
              className="bio-preview__more"
              onClick={onMore}
              aria-label="Show full bio"
            >
              More
            </button>
          </>
        )}
      </p>
    </div>
  );
};

export default BioPreview;
